import React, { useEffect, useState, useCallback } from "react";
import {
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogActions,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  DialogContent,
  DialogContentText,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import CheckIcon from "@mui/icons-material/Check";
import axios from "axios";
import { getValidAccessToken } from "../../utils/tokenUtils";
import { Download, FileOpen } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

const apiUrl = process.env.REACT_APP_API_URL;

const TaxDraft = ({ ticketId, userType, ticketData, setTicketData }) => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [openDialog, setOpenDialog] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchFolders = useCallback(async () => {
    const token = await getValidAccessToken();
    try {
      const response = await axios.get(
        `${apiUrl}/folders/?ticket_id=${ticketId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data) {
        setFolders(response.data);
      } else {
        setErrorMessage("Unable to fetch folders.");
      }
    } catch (error) {
      setErrorMessage("Error fetching folders.");
      console.error("Error fetching folders:", error);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const handleFolderStatusUpdate = async (folderId, status) => {
    const token = await getValidAccessToken();
    try {
      const response = await axios.get(
        `${apiUrl}/folders/update-status/?folder_status=${status}&ticket_id=${ticketId}&folder_id=${folderId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        setTicketData((prevData) => ({
          ...prevData,
          ticket_status: response.data.ticket_status,
        }));
        fetchFolders(); // Refresh folder list
      } else {
        setErrorMessage("Unable to update folder status.");
      }
    } catch (error) {
      setErrorMessage("Error updating folder status.");
      console.error("Error updating folder status:", error);
    }
  };

  const handleFolderClick = async (folderId) => {
    const token = await getValidAccessToken();
    try {
      const response = await axios.get(
        `${apiUrl}/folders/files/?ticket_id=${ticketId}&folder_id=${folderId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        setFiles(response.data.data);
        setSelectedFolder(folderId);
      } else {
        setErrorMessage("Unable to fetch files.");
      }
    } catch (error) {
      setErrorMessage("Error fetching files.");
      console.error("Error fetching files:", error);
    }
  };

  const handleFileUpload = async (event, folderId) => {
    const token = await getValidAccessToken();
    const files = event.target.files; // Get all the selected files
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      // Iterate over the selected files and upload them one by one
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        const formData = new FormData();
        formData.append("file", file);
        formData.append("ticket_id", ticketId);
        formData.append("folder_id", folderId);

        const response = await axios.post(
          `${apiUrl}/folders/files/`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.data.success) {
          setErrorMessage("Unable to upload file.");
          break; // Stop uploading further files if any file upload fails
        }
      }

      // If all files were uploaded successfully, refresh the folder
      handleFolderClick(folderId);
    } catch (error) {
      setErrorMessage("Error uploading files.");
      console.error("Error uploading files:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async () => {
    const token = await getValidAccessToken();
    try {
      const response = await axios.post(
        `${apiUrl}/folders/`,
        {
          ticket_id: ticketId,
          folder_name: newFolderName,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        fetchFolders(); // Refresh folder list
        setOpenDialog(false);
        setNewFolderName("");
      } else {
        setErrorMessage("Unable to create folder.");
      }
    } catch (error) {
      setErrorMessage("Error creating folder.");
      console.error("Error creating folder:", error);
    }
  };

  const handleView = async (fileId, fileName) => {
    const token = await getValidAccessToken();
    try {
      const response = await axios.get(
        `${apiUrl}/draft-file-download/${fileId}/?folder_id=${selectedFolder}&ticket_id=${ticketId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob", // Make
        }
      );
      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);

      window.open(blobUrl, "_blank");
    } catch (error) {
      setErrorMessage("Error verifying document.");
      console.error("Error verifying document:", error);
    }
  };
  const handleDownload = async (fileId, fileName) => {
    const token = await getValidAccessToken();
    try {
      const response = await axios.get(
        `${apiUrl}/draft-file-download/${fileId}/?folder_id=${selectedFolder}&ticket_id=${ticketId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob", // Make
        }
      );
      const contentDisposition = response.headers.get("Content-Disposition");
      if (contentDisposition && contentDisposition.includes("filename=")) {
        fileName = contentDisposition.split("filename=")[1].replace(/"/g, ""); // Remove quotes around filename if present
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName); // Set file name
      document.body.appendChild(link);
      link.click(); // Trigger the download
      link.parentNode.removeChild(link); // Clean up
      window.URL.revokeObjectURL(url); // Release URL object
    } catch (error) {
      setErrorMessage("Error verifying document.");
      console.error("Error verifying document:", error);
    }
  };

  const handleDelete = async (fileId) => {
    const token = await getValidAccessToken();
    try {
      const response = await axios.get(
        `${apiUrl}/draft-file-delete/${fileId}/?folder_id=${selectedFolder}&ticket_id=${ticketId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        await handleFolderClick(selectedFolder);
      } else {
        setErrorMessage("Unable to Delete file details.");
      }
    } catch (error) {
      setErrorMessage("Error verifying document.");
      console.error("Error verifying document:", error);
    }
  };
  const handleContextMenu = (event) => {
    event.preventDefault(); // prevent browser context menu
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleDeleteClick = () => {
    // Implement delete folder logic here
    setShowDeleteConfirm(true);
  };

  const handleRenameClick = async (folder_id) => {
    const token = await getValidAccessToken();
    try {
      const response = await axios.post(
        `${apiUrl}/folders/rename/`,
        {
          folder_id: folder_id,
          new_name: newFolderName,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        fetchFolders(); // Refresh folder list
        setOpenDialog(false);
        setAnchorEl(null);
        setNewFolderName("");
      } else {
        setErrorMessage("Unable to rename folder.");
      }
    } catch (error) {
      setErrorMessage("Error renaming folder.");
      console.error("Error renaming folder:", error);
    }
  };
  const handleDeleteFolder = async (folderId) => {
    const token = await getValidAccessToken();
    try {
      const response = await axios.post(
        `${apiUrl}/folders/delete/`,
        {
          folder_id: folderId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.success) {
        fetchFolders(); // Refresh folder list
        setShowDeleteConfirm(false);
        setAnchorEl(null);
      } else {
        setErrorMessage("Unable to delete folder.");
      }
    } catch (error) {
      setErrorMessage("Error deleting folder.");
      console.error("Error deleting folder:", error);
    }
  };

  return (
    <Box>
      {errorMessage && (
        <Typography variant="body2" color="error">
          {errorMessage}
        </Typography>
      )}

      {(userType !== "user" || userType === "admin") && !selectedFolder && (
        <Button
          variant="contained"
          onClick={() => setOpenDialog(true)}
          sx={{ mb: 2 }}
        >
          Create New Folder
        </Button>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Create a New Folder</DialogTitle>
        <TextField
          autoFocus
          margin="dense"
          label="Folder Name"
          fullWidth
          variant="standard"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
        />
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateFolder} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {!selectedFolder ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "secondary.main",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                  }}
                >
                  Folder Name
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "secondary.main",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                  }}
                >
                  Creation Date
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "secondary.main",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: "secondary.main",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.875rem",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {folders.map((folder) => (
                <TableRow key={folder.folder_id}>
                  <TableCell>
                    {isEditing ? (
                      <>
                        <TextField
                          value={newFolderName || folder.folder_name}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          size="small"
                        />
                        <IconButton
                          onClick={() => {
                            setAnchorEl(null);
                            setIsEditing(false);
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            handleRenameClick(folder.folder_id);
                            setIsEditing(false);
                          }}
                          color="primary"
                        >
                          <CheckIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <Button
                          startIcon={<FolderIcon />}
                          onClick={() => handleFolderClick(folder.folder_id)}
                          onContextMenu={handleContextMenu}
                        >
                          {folder.folder_name}
                        </Button>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={handleClose}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                        >
                          <MenuItem onClick={() => setIsEditing(true)}>
                            Rename
                          </MenuItem>
                          <MenuItem onClick={handleDeleteClick}>
                            Delete
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                    <Dialog
                      open={showDeleteConfirm}
                      onClose={() => setShowDeleteConfirm(false)}
                    >
                      <DialogTitle>Confirm Deletion</DialogTitle>
                      <DialogContent>
                        <DialogContentText>
                          Are you sure you want to delete this folder?
                          <br />
                          <b style={{ color: "red" }}>Note</b>. This action
                          cannot be undone. By clicking delete, all files within
                          the folder will also be removed.
                        </DialogContentText>
                      </DialogContent>
                      <DialogActions>
                        <Button
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setAnchorEl(null);
                          }}
                          color="primary"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={() => handleDeleteFolder(folder.folder_id)}
                          color="secondary"
                        >
                          Delete
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </TableCell>
                  <TableCell>
                    {new Date(folder.creation_date).toLocaleString()}
                  </TableCell>
                  <TableCell>{folder.folder_status}</TableCell>
                  <TableCell>
                    {userType === "user" && folder.folder_status === "Sent" && (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() =>
                            handleFolderStatusUpdate(
                              folder.folder_id,
                              "Approved"
                            )
                          }
                        >
                          Approve
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() =>
                            handleFolderStatusUpdate(
                              folder.folder_id,
                              "Rejected"
                            )
                          }
                        >
                          Reject
                        </Button>
                      </>
                    )}
                    {(userType !== "user" || userType === "admin") &&
                      ["Pending", "Rejected"].includes(
                        folder.folder_status
                      ) && (
                        <Button
                          variant="contained"
                          onClick={() =>
                            handleFolderStatusUpdate(folder.folder_id, "Sent")
                          }
                        >
                          Sent
                        </Button>
                      )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setSelectedFolder(null)}
              sx={{ mr: 2 }}
            >
              Back to Folders
            </Button>
            {(userType !== "user" || userType === "admin") && (
              <Box sx={{ display: "flex", justifyContent: "right", mb: 0 }}>
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, selectedFolder)}
                  style={{ display: "none" }}
                  id="upload-file"
                  multiple
                />
                <label htmlFor="upload-file" style={{ cursor: "pointer" }}>
                  <Box
                    sx={{
                      border: "2px dashed #1976d2",
                      borderRadius: "8px",
                      padding: "30px",
                      width: "400px",
                      height: "100px",
                      textAlign: "center",
                      backgroundColor: "#f9f9f9",
                      color: "#1976d2",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        borderColor: "#1565c0",
                      },
                    }}
                  >
                    {" "}
                    {uploading ? "Uploading..." : "Upload/Drop files here"}
                  </Box>
                </label>
              </Box>
            )}
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "secondary.main",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                    }}
                  >
                    File ID
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "secondary.main",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                    }}
                  >
                    Filename
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "secondary.main",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                    }}
                  >
                    Uploaded Date
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "secondary.main",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "0.875rem",
                    }}
                  >
                    Auctions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.file_id}>
                    <TableCell>{file.file_id}</TableCell>
                    <TableCell>{file.file_name}</TableCell>
                    <TableCell>
                      {new Date(file.upload_date).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.875rem" }}>
                      <IconButton
                        onClick={() => handleView(file.file_id, file.file_name)}
                      >
                        <FileOpen color="success" />
                      </IconButton>
                      <IconButton
                        onClick={() =>
                          handleDownload(file.file_id, file.file_name)
                        }
                      >
                        <Download color="success" />
                      </IconButton>
                      {(userType !== "user" || userType === "admin") && (
                        <IconButton onClick={() => handleDelete(file.file_id)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default TaxDraft;
